import { useEffect } from 'react'

import { useMemoizedFn } from 'ahooks'

import { pick } from '@eastcoal/common/utils/object'

import { useRichEditorStore } from '../../Store/hooks/useStore'
import { useSelectionStyle } from '../useSelectionStyle'

const findClosestValue = (arr: number[], target: number) => {
  let closest = arr[0]
  let minDiff = Math.abs(target - closest)

  for (let i = 1; i < arr.length; i++) {
    const diff = Math.abs(target - arr[i])
    if (diff < minDiff) {
      closest = arr[i]
      minDiff = diff
    }
  }

  return closest
}

export const useFontSize = () => {
  const { defaultFontSize, currentFontSize, fontSizeList, setCurrentFontSize } =
    useRichEditorStore(state =>
      pick(state, [
        'defaultFontSize',
        'currentFontSize',
        'fontSizeList',
        'setCurrentFontSize'
      ])
    )
  const [fontSize, _setFontSize] = useSelectionStyle({
    name: 'font-size',
    defaultValue: `${defaultFontSize}px`
  })

  const fontSizeValue = fontSize ? Number.parseInt(fontSize) : defaultFontSize

  useEffect(() => {
    setCurrentFontSize(fontSizeValue)
  }, [fontSizeValue, setCurrentFontSize])

  const setFontSizeValue = useMemoizedFn((value: number | string) => {
    _setFontSize(`${value}px`)
  })

  const pickFontSize = (indexGetter: (index: number) => number) => {
    const fontSizeValueList = fontSizeList.map(item =>
      Number.parseFloat(`${item.value}`)
    )
    const currentFontSize = findClosestValue(fontSizeValueList, fontSizeValue)
    const index = fontSizeValueList.indexOf(currentFontSize)
    const nextIndex = indexGetter(index)
    const nextFontSize = fontSizeValueList[nextIndex] ?? currentFontSize
    setFontSizeValue(nextFontSize)
  }

  const increaseFontSize = useMemoizedFn(() => {
    pickFontSize(index => index - 1)
  })

  const decreaseFontSize = useMemoizedFn(() => {
    pickFontSize(index => index + 1)
  })

  return {
    fontSizeList,
    increaseFontSize,
    decreaseFontSize,
    fontSize: currentFontSize,
    setFontSize: setFontSizeValue
  }
}
